import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Icon,
  useTheme,
} from "@mui/material";
import moment from "moment";
import FetchHook from "../../Api/FetchHooks";
import PageLoader from "../../Components/PageLoader";

// Import all the icons
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CreditScoreOutlinedIcon from "@mui/icons-material/CreditScoreOutlined";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import AdminStatCard from "./Components/AdminStatCard";
import Page from "../../Components/Page";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../../Components/DateRangePicker";
import AdminLoanCharts from "./Components/AdminLoanCharts";

// --- CARD CONFIGURATION ---
const cardConfig = {
  loan_taken: {
    title: "Total Loans Taken",
    icon: CreditScoreOutlinedIcon,
    color: "primary",
    path: "/loans",
  },
  loan_repaid: {
    title: "Total Loans Repaid",
    icon: AddCardOutlinedIcon,
    color: "success",
    path: "/loans?status=repaid",
  },
  // Add more finance specific cards if available in API response
};

const FinanceDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [startDate, setStartDate] = useState(new Date("2025-01-01"));
  const [endDate, setEndDate] = useState(new Date());
  const [loans, setLoans] = useState([]);

  const [loading, response, request] = FetchHook();
  const [loanLoading, loanResponse, loanRequest] = FetchHook();

  useEffect(() => {
    handleSearch();
    fetchLoans();
  }, []);

  const handleSearch = async () => {
    const start = moment(startDate).format("YYYY-MM-DD");
    const end = moment(endDate).format("YYYY-MM-DD");
    await request({
      url: `/dashreport?start_date='${start}'&end_date='${end}'`,
    });
  };

  const fetchLoans = useCallback(async () => {
    await loanRequest({ url: `loan` });
  }, []);

  useEffect(() => {
    if (loanResponse?.data?.Loan) setLoans(loanResponse.data.Loan);
  }, [loanResponse]);

  return (
    <Page title={"Finance Dashboard"}>
      {loading ? (
        <PageLoader />
      ) : response?.data?.Dashboard ? (
        <Grid container spacing={3}>
          {/* Stat Cards */}
          <Grid item xs={12} md={12}>
            <Card>
              <CardContent>
                <Box sx={{ my: 2 }}>
                  <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    onSearch={handleSearch}
                    loading={loading}
                  />
                </Box>

                <Grid container spacing={2}>
                  {response.data.Dashboard.map((item) => {
                    const config = cardConfig[item.label];
                    if (!config) return null;

                    // Calculate percentage if needed, or just pass 0 or calculated value
                    // For finance, maybe we compare against a target or total?
                    // For now, keeping it simple

                    return (
                      <Grid item key={item.label} xs={12} sm={6} md={6} lg={3}>
                        <AdminStatCard
                          title={config.title}
                          count={item.count}
                          icon={config.icon}
                          color={config.color}
                          percentage={0} // Placeholder or calculate if possible
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* AdminLoanCharts - Reusing for Finance as it shows loan data */}
          {loanLoading ? (
            <Grid item xs={12}>
              <PageLoader />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <AdminLoanCharts loans={loans} />
            </Grid>
          )}
        </Grid>
      ) : (
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon sx={{ fontSize: 80, color: "grey.400" }}>
            <CancelOutlinedIcon fontSize="inherit" />
          </Icon>
          <Typography variant="h6" color="text.secondary">
            {response?.response?.data?.StatusMessage || "Could not load data."}
          </Typography>
        </Box>
      )}
    </Page>
  );
};

export default FinanceDashboard;
